/**
 * Streaming utilities for Durin AI client
 */

import { StreamingChunk, NDJSONWrapper } from '../types';
import { DurinStreamError } from './errors';

export class StreamParser {
  private buffer = '';
  private isNDJSON = false;

  constructor(isNDJSON = false) {
    this.isNDJSON = isNDJSON;
  }

  /**
   * Parse incoming stream data and return complete chunks
   */
  parse(data: string): StreamingChunk[] {
    this.buffer += data;
    
    if (this.isNDJSON) {
      return this.parseNDJSON();
    } else {
      return this.parseRawJSON();
    }
  }

  private parseNDJSON(): StreamingChunk[] {
    const chunks: StreamingChunk[] = [];
    const lines = this.buffer.split('\n');
    
    // Keep the last line in buffer (might be incomplete)
    this.buffer = lines.pop() || '';
    
    for (const line of lines) {
      if (line.trim()) {
        try {
          const wrapper: NDJSONWrapper = JSON.parse(line);
          chunks.push(wrapper.data);
        } catch (error) {
          throw new DurinStreamError(`Failed to parse NDJSON line: ${line}`, error as Error);
        }
      }
    }
    
    return chunks;
  }

  private parseRawJSON(): StreamingChunk[] {
    const chunks: StreamingChunk[] = [];
    
    // Split on newlines and try to parse each complete JSON object
    const lines = this.buffer.split('\n');
    this.buffer = lines.pop() || '';
    
    for (const line of lines) {
      if (line.trim()) {
        try {
          const chunk: StreamingChunk = JSON.parse(line);
          chunks.push(chunk);
        } catch (error) {
          throw new DurinStreamError(`Failed to parse JSON chunk: ${line}`, error as Error);
        }
      }
    }
    
    return chunks;
  }

  /**
   * Check if there's remaining data in the buffer
   */
  hasRemainingData(): boolean {
    return this.buffer.trim().length > 0;
  }

  /**
   * Get any remaining data (for debugging)
   */
  getRemainingBuffer(): string {
    return this.buffer;
  }
}

/**
 * Create an async iterable from a ReadableStream
 */
export async function* createStreamIterable(
  stream: ReadableStream<Uint8Array>,
  isNDJSON = false,
  signal?: AbortSignal
): AsyncIterable<StreamingChunk> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const parser = new StreamParser(isNDJSON);
  
  try {
    while (true) {
      // Check for abort signal
      if (signal?.aborted) {
        throw new DurinStreamError('Stream aborted');
      }
      
      const { done, value } = await reader.read();
      
      if (done) {
        // Check for any remaining buffered data
        if (parser.hasRemainingData()) {
          console.warn('Stream ended with unparsed data:', parser.getRemainingBuffer());
        }
        break;
      }
      
      const text = decoder.decode(value, { stream: true });
      const chunks = parser.parse(text);
      
      for (const chunk of chunks) {
        yield chunk;
      }
    }
  } catch (error) {
    if (error instanceof DurinStreamError) {
      throw error;
    }
    throw new DurinStreamError('Stream processing error', error as Error);
  } finally {
    reader.releaseLock();
  }
}

/**
 * Collect all chunks from a stream into a single response
 */
export async function collectStreamResponse(
  streamIterable: AsyncIterable<StreamingChunk>
): Promise<{ content: string; finalChunk?: StreamingChunk }> {
  let content = '';
  let finalChunk: StreamingChunk | undefined;
  
  for await (const chunk of streamIterable) {
    switch (chunk.type) {
      case 'content':
        if (chunk.delta) {
          content += chunk.delta;
        }
        break;
      case 'end':
        finalChunk = chunk;
        break;
    }
  }
  
  return finalChunk ? { content, finalChunk } : { content };
}